import { NativeModules } from 'react-native';

/**
 * The native handle on the screen, during a call.
 *
 * ---------------------------------------------------------------------------
 * WHY A NATIVE MODULE AT ALL
 * ---------------------------------------------------------------------------
 * Two behaviours a phone call has that an app does not get for free, and
 * neither is reachable from JavaScript on either platform:
 *
 *   - The screen blanks while the handset is at an ear, so a cheek cannot
 *     press Hang up, and comes back the moment it is taken away.
 *   - The screen does not lock itself mid-conversation.
 *
 * Android needs a `PROXIMITY_SCREEN_OFF_WAKE_LOCK` and a window flag; iOS
 * needs `isProximityMonitoringEnabled` and `isIdleTimerDisabled`. See
 * `android/app/src/main/java/com/homingo/callscreen/CallScreenModule.kt` and
 * `ios/Homingo/CallScreen.swift`. The Pro app carries the same four files.
 *
 * ---------------------------------------------------------------------------
 * MISSING IS A NORMAL STATE, NOT AN ERROR
 * ---------------------------------------------------------------------------
 * A JavaScript bundle can run against a native binary older than itself - a
 * dev client that has not been rebuilt, or a store build mid-rollout. When
 * that happens the module is simply absent, and every function here becomes a
 * no-op that resolves.
 *
 * That asymmetry is deliberate. Losing the screen behaviour costs a slightly
 * worse call; throwing here would cost the call itself, on a feature the whole
 * point of which is that two people can talk. A voice feature must never be
 * broken by its own comfort layer.
 */

type CallScreenNative = {
  /**
   * Whether the screen should blank when something is close to the sensor.
   *
   * Resolves `false` when the handset has no proximity sensor at all - plenty
   * of budget devices do not - which is different from the caller having
   * turned it off, and worth being able to tell apart.
   */
  setProximityEnabled(on: boolean): Promise<boolean>;
  /** Whether the screen may lock itself on the idle timer. */
  setKeepAwake(on: boolean): Promise<void>;
};

const native = (NativeModules as Record<string, unknown>).CallScreen as
  | CallScreenNative
  | undefined;

/** Whether the native side is present in this binary. */
export const callScreenAvailable = native !== undefined;

/**
 * Ask for the proximity blanking.
 *
 * The caller passes only its own half of the decision - a call is up and the
 * earpiece is the intended output. **Where the audio is actually going is the
 * native side's business**, because it is the only side that can see it:
 * somebody on a Bluetooth headset has the handset in a pocket with the sensor
 * covered, and blanking there would be exactly backwards.
 *
 * Resolves `false` when there is no sensor or no native module.
 */
export function setCallProximity(on: boolean): Promise<boolean> {
  if (!native) return Promise.resolve(false);
  return native.setProximityEnabled(on).catch(() => false);
}

/**
 * Hold the screen awake, or let it go.
 *
 * The other half of the pair, and the one that is easy to forget: without it
 * a call on speaker locks the handset thirty seconds in, which no phone call
 * has ever done.
 */
export function setCallKeepAwake(on: boolean): Promise<void> {
  if (!native) return Promise.resolve();
  return native.setKeepAwake(on).catch(() => undefined);
}
